import React, { useMemo, useState, useEffect, useRef } from "react";
import Table from "../../../core/common/dataTable/index";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

type EmployeeRow = {
  id: string;
  empNo: string;
  name: string;
  category?: string;
  designation: string;
  departmentName?: string;
  joiningDate?: string;
  lastPromoDate?: string;
  serviceYears: number | string;
  aparTotal: string;
  aparRemarks?: string;
  dar: string;
  vigilance: string;
  effectiveDate: string;
  listClass: string;
  dpcStatus: string;
  dpcRemarks: string;
  [key: string]: any;
};

const escapeXml = (unsafe: any): string => {
  if (unsafe === null || unsafe === undefined) return "";
  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

const escapeExcelXml = (val: any) => {
  if (val === undefined || val === null) return "";
  return String(val)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;")
    .replace(/\n/g, "&#10;");
};

const cleanCellText = (str: any) => {
  if (str === null || str === undefined) return "";
  return String(str)
    .replace(/<br[^>]*>/gi, "\n")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
};

const formatAparScore = (val: any): string => {
  if (val === undefined || val === null || val === "" || val === "—") return "-";
  const str = String(val).trim();
  const num = parseFloat(str);
  if (!isNaN(num) && num === 0) return "-";
  if (str === "0" || str === "0.0" || str === "0.00") return "-";
  return str;
};

const searchEmployeeByNumber = async (empNumber: string) => {
  const trimmed = empNumber.trim();
  if (!trimmed) return null;

  // 1. Try GET /promotion/search-employee?empNumber=...
  try {
    const res: any = await Api.get(API_URLS.PROMOTION.SEARCH_EMPLOYEE, { empNumber: trimmed, empId: trimmed, search: trimmed });
    const payload = res?.data?.data || res?.data || res?.employee || res;
    if (payload && (payload.employee_name || payload.employeeName || payload.name)) {
      return {
        empNo: payload.emp_no || payload.empId || trimmed,
        name: payload.employee_name || payload.employeeName || payload.name || "",
        designation: payload.designation || payload.present_designation || payload.desig || ""
      };
    }
  } catch (e) {
    // try POST fallback
  }

  // 2. Try POST /promotion/search-employee { empNumber: ..., empId: ... }
  try {
    const res: any = await Api.post(API_URLS.PROMOTION.SEARCH_EMPLOYEE, { empNumber: trimmed, empId: trimmed });
    const payload = res?.data?.data || res?.data || res?.employee || res;
    if (payload && (payload.employee_name || payload.employeeName || payload.name)) {
      return {
        empNo: payload.emp_no || payload.empId || trimmed,
        name: payload.employee_name || payload.employeeName || payload.name || "",
        designation: payload.designation || payload.present_designation || payload.desig || ""
      };
    }
  } catch (e) {
    // try path param fallback
  }

  // 3. Try GET /promotion/employee/:empNumber
  try {
    const directUrl = `${API_URLS.PROMOTION.EMPLOYEE_BY_NUMBER}${trimmed}`;
    const res: any = await Api.get(directUrl, null);
    const payload = res?.data?.data || res?.data || res?.employee || res;
    if (payload && (payload.employee_name || payload.employeeName || payload.name)) {
      return {
        empNo: payload.emp_no || payload.empId || trimmed,
        name: payload.employee_name || payload.employeeName || payload.name || "",
        designation: payload.designation || payload.present_designation || payload.desig || ""
      };
    }
  } catch (e) {}

  return null;
};

const DpcReview: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const stateCycleId = location.state?.cycleId;
  const paramCycleId = queryParams.get("cycleId");
  const initialCycleId = stateCycleId || (paramCycleId ? Number(paramCycleId) : 1);

  const [selectedCycleId, setSelectedCycleId] = useState<number>(initialCycleId);
  const [query, setQuery] = useState<string>("");
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cyclesList, setCyclesList] = useState<any[]>([
    { id: 1, name: " " }
  ]);

  const [selectedEvaluationCandidate, setSelectedEvaluationCandidate] = useState<any | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<"apar" | "dar" | "vigilance">("apar");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const tableRef = useRef<any>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [excludingId, setExcludingId] = useState<string | null>(null);
  const [selectedBulkRemark, setSelectedBulkRemark] = useState<string>("");
  const [unselectedBulkRemark, setUnselectedBulkRemark] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<"ALL" | "RECOMMENDED" | "NOT_RECOMMENDED">("ALL");

  type CommitteeMember = {
    empNo: string;
    name: string;
    designation: string;
  };

  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [committeeMembers, setCommitteeMembers] = useState<CommitteeMember[]>([
    { empNo: "", name: "", designation: "" },
    { empNo: "", name: "", designation: "" },
    { empNo: "", name: "", designation: "" }
  ]);
  const [searchingIndex, setSearchingIndex] = useState<number | null>(null);

  const handleSearchMember = async (index: number) => {
    const empNo = (committeeMembers[index]?.empNo || "").trim();
    if (!empNo) {
      toast.warning(`Please enter Employee No. for Member ${index + 1}`);
      return;
    }

    setSearchingIndex(index);
    try {
      // 1. Check local candidates list first
      const unpadded = empNo.replace(/^0+/, "");
      const localMatch = parsedEmployees.find((e: any) => {
        const eNo = String(e.empNo || e.empId || e.emp_no || "").trim();
        return eNo === empNo || eNo.replace(/^0+/, "") === unpadded;
      });

      if (localMatch) {
        const mName = localMatch.employeeName || localMatch.name || localMatch.empName || "";
        const mDesig = localMatch.designation || localMatch.present_designation || localMatch.oldDesignation || "";
        if (mName) {
          setCommitteeMembers((prev) => {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              name: mName,
              designation: mDesig
            };
            return updated;
          });
          toast.success(`Found Member ${index + 1}: ${mName}`);
          setSearchingIndex(null);
          return;
        }
      }

      // 2. Query /promotion/search-employee
      const found = await searchEmployeeByNumber(empNo);
      if (found && found.name) {
        setCommitteeMembers((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            name: found.name,
            designation: found.designation
          };
          return updated;
        });
        toast.success(`Found Member ${index + 1}: ${found.name}`);
      } else {
        toast.error(`Employee ${empNo} not found in promotion records.`);
      }
    } catch (err: any) {
      toast.error("Failed to search employee.");
    } finally {
      setSearchingIndex(null);
    }
  };

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [selectedCycleId, query, activeFilter]);

  // Load initiated cycles list from API or fallback to localStorage
  useEffect(() => {
    const loadCycles = async () => {
      let loaded = false;
      try {
        const response = await Api.get(API_URLS.PROMOTION.CYCLES, true);
        console.log("CYCLES API Response (DPC Review):", response);
        if (response && response.data && Array.isArray(response.data.data)) {
          const rawList = response.data.data;
          const list = rawList.map((c: any) => ({
            id: c.id,
            name: c.cycleName || (c.gradeTo ? `${c.gradeFrom} to ${c.gradeTo} Promotion - ${c.quarter} ${c.financialYear}` : `${c.gradeFrom || ""} Promotion - ${c.quarter} ${c.financialYear}`),
            status: c.status
          }));
          setCyclesList(list);
          loaded = true;
          
          if (list.length > 0) {
            const queryId = queryParams.get("cycleId");
            if (!queryId && !location.state?.cycleId) {
              setSelectedCycleId(list[0].id);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch cycles from API, falling back to local storage", error);
      }

      if (!loaded) {
        const stored = localStorage.getItem("promotion_cycles");
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const hasCycle1 = parsed.some((c: any) => c.id === 1);
              const list = hasCycle1 ? parsed : [{ id: 1, name: " " }, ...parsed];
              setCyclesList(list);
            }
          } catch (e) {
            console.error("Failed to parse promotion cycles from localStorage", e);
          }
        }
      }
    };
    loadCycles();
  }, []);

  // Sync state if URL / state changes externally
  useEffect(() => {
    const stateId = location.state?.cycleId;
    const queryId = queryParams.get("cycleId");
    const currentId = stateId || (queryId ? Number(queryId) : 1);
    if (currentId !== selectedCycleId) {
      setSelectedCycleId(currentId);
    }
  }, [location.state, location.search]);

  // Fetch employees checklist for the selected cycle using EVALUATE_TWO_STAGE
  const fetchChecks = async () => {
    setLoading(true);
    try {
      const payload = {
        Request: {
          data: {
            cycleId: Number(selectedCycleId)
          }
        }
      };
      const response = await Api.post(API_URLS.PROMOTION.EVALUATE_TWO_STAGE, payload, true);
      console.log("EVALUATE_TWO_STAGE Response (for DPC Review):", response);

      const resData = response?.data || response;
      const innerData = resData?.data || resData;
      let dataToSet: any[] = [];
      if (innerData && Array.isArray(innerData.eligibleByServiceWithEvaluations)) {
        dataToSet = innerData.eligibleByServiceWithEvaluations;
      } else if (resData && Array.isArray(resData.eligibleByServiceWithEvaluations)) {
        dataToSet = resData.eligibleByServiceWithEvaluations;
      } else if (innerData?.data && Array.isArray(innerData.data.eligibleByServiceWithEvaluations)) {
        dataToSet = innerData.data.eligibleByServiceWithEvaluations;
      } else if (Array.isArray(innerData)) {
        dataToSet = innerData;
      } else if (innerData && Array.isArray(innerData.data)) {
        dataToSet = innerData.data;
      } else if (resData && Array.isArray(resData.data)) {
        dataToSet = resData.data;
      } else if (typeof innerData === 'object' && innerData !== null) {
        const rawMerged: any[] = [];
        Object.keys(innerData).forEach(key => {
          if (Array.isArray(innerData[key])) {
            rawMerged.push(...innerData[key]);
          }
        });
        dataToSet = rawMerged;
      }

      setEmployees(dataToSet);
    } catch (error) {
      console.error("Failed to fetch checks", error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChecks();
  }, [selectedCycleId]);

  const handleCycleChange = (id: number) => {
    setSelectedCycleId(id);
    navigate(`${location.pathname}?cycleId=${id}`, { replace: true, state: { cycleId: id } });
  };

  const formatDateDMY = (dateStr: any) => {
    if (!dateStr || dateStr === "N/A" || dateStr === "-" || dateStr === "null" || dateStr === "undefined") return "—";
    const str = String(dateStr).trim();
    if (!str) return "—";
    if (str.includes("T")) {
      const ymd = str.split("T")[0];
      const parts = ymd.split("-");
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    if (str.includes("-")) {
      const parts = str.split("-");
      if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      if (parts.length === 3 && parts[2].length === 4) return str;
    }
    return str;
  };

  const getEffectivePromotionDate = (e: any) => {
    const raw =
      e.adjustedPromotionDate ??
      e.adjusted_promotion_date ??
      e.effectivePromotionDate ??
      e.effective_promotion_date ??
      e.effectiveEligibilityDate ??
      e.effective_eligibility_date ??
      e.eligibilityDate ??
      e.baseDate ??
      e.base_date ??
      e.promotion_effective_date ??
      e.promotionEffectiveDate ??
      e.effectiveDate;

    if (!raw || raw === "N/A" || raw === "null" || raw === "undefined" || raw === "-") return "—";
    return formatDateDMY(raw);
  };

  const getDarBadgeDetails = (e: any) => {
    const rawStatus = (e.darStatus ?? e.dar_status ?? e.dar_evaluation?.status ?? e.dar ?? "").toString().trim();
    const statusLower = rawStatus.toLowerCase();

    if (statusLower === "dar clear" || statusLower === "dar_clear" || statusLower === "clear" || statusLower === "cleared" || statusLower === "passed") {
      return {
        text: "DAR CLEAR",
        badgeClass: "bg-success text-white",
        badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "chargesheet issued" || statusLower === "chargesheet_issued" || statusLower === "chargesheetissued") {
      return {
        text: "ChargeSheet Issued",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "minor penalty" || statusLower === "minor_penalty" || statusLower === "minorpenalty") {
      return {
        text: "Minor Penalty",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fefce8", color: "#a16207", border: "1.5px solid #fde047", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "major penalty" || statusLower === "major_penalty" || statusLower === "majorpenalty") {
      return {
        text: "Major Penalty",
        badgeClass: "bg-danger text-white",
        badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "pending" || statusLower === "false") {
      return {
        text: "Pending",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    return {
      text: rawStatus || "—",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
    };
  };

  const getVigilanceBadgeDetails = (e: any) => {
    const rawStatus = (e.vigilance_evaluation?.status ?? e.vigilanceStatus ?? e.vigilance_status ?? e.vigilanceType ?? e.vigilance_type ?? e.vigilance ?? "").toString().trim();
    const statusLower = rawStatus.toLowerCase();

    if (statusLower === "no vigilance case" || statusLower === "no_vigilance_case" || statusLower === "clear" || statusLower === "cleared" || statusLower === "passed") {
      return {
        text: "No Vigilance Case",
        badgeClass: "bg-success text-white",
        badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "vigilance initiated" || statusLower === "vigilance_initiated") {
      return {
        text: "Vigilance Initiated",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "penalty awarded" || statusLower === "penalty_awarded") {
      return {
        text: "Penalty Awarded",
        badgeClass: "bg-danger text-white",
        badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "pending" || statusLower === "false") {
      return {
        text: "Pending",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    return {
      text: rawStatus || "—",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
    };
  };

  const formatEmployeeRow = (e: any, idx: number): EmployeeRow => {
    let joiningDate = e.joiningDate || e.joining_date || e.date_of_joining || e.dateOfJoining || e.doj || "";
    let lastPromoDate = e.lastPromoDate || e.last_promotion_date || e.last_promo_date || e.lastPromotion || "";
    const combined = e.joiningOrLastPromoDate || e.joining_or_last_promo_date || "";

    if ((!joiningDate || !lastPromoDate) && combined) {
      if (typeof combined === "string" && combined.includes("/")) {
        const parts = combined.split("/").map((s: string) => s.trim());
        if (parts.length >= 2) {
          if (!joiningDate) joiningDate = parts[0];
          if (!lastPromoDate) lastPromoDate = parts[1];
        } else if (parts.length === 1 && !joiningDate) {
          joiningDate = parts[0];
        }
      } else if (typeof combined === "string" && combined.includes(",")) {
        const parts = combined.split(",").map((s: string) => s.trim());
        if (parts.length >= 2) {
          if (!joiningDate) joiningDate = parts[0];
          if (!lastPromoDate) lastPromoDate = parts[1];
        } else if (parts.length === 1 && !joiningDate) {
          joiningDate = parts[0];
        }
      } else if (!joiningDate) {
        joiningDate = combined;
      }
    }

    const formatJoiningDate = (val: any) => {
      if (!val || val === "N/A" || val === "-" || val === "null" || val === "undefined") return "—";
      if (typeof val === "string" && val.includes("T")) return formatDateDMY(val.split("T")[0]);
      return formatDateDMY(val);
    };

    const formatLastPromoDate = (val: any) => {
      if (!val || val === "N/A" || val === "-" || val === "null" || val === "undefined") return "No Data Found";
      if (typeof val === "string" && val.includes("T")) return formatDateDMY(val.split("T")[0]);
      return formatDateDMY(val);
    };

    let category =
      e.category ||
      e.emp_category ||
      e.empCategory ||
      e.caste_category ||
      e.casteCategory ||
      e.cast_category ||
      e.castCategory ||
      e.caste ||
      e.social_category ||
      e.socialCategory ||
      "—";
    if (category === "N/A" || category === "null" || category === "undefined") category = "—";

    let desig = e.oldDesignation || e.old_designation || e.designationName || e.designation || e.newDesignation || e.new_designation || e.designationID || "—";
    if (typeof desig === "string" && desig.includes("-")) {
      const parts = desig.split("-").filter((p: string) => p !== "P");
      if (parts.length > 0) desig = parts.join("-");
    }

    let aparTotal = "—";
    if (e.apar_evaluation?.totalScore !== undefined && e.apar_evaluation?.totalScore !== null) {
      aparTotal = String(e.apar_evaluation.totalScore);
    } else if (e.apar_evaluation?.total_score !== undefined && e.apar_evaluation?.total_score !== null) {
      aparTotal = String(e.apar_evaluation.total_score);
    } else if (e.aparTotalScore !== undefined && e.aparTotalScore !== null) {
      aparTotal = String(e.aparTotalScore);
    } else if (e.apar_total_score !== undefined && e.apar_total_score !== null) {
      aparTotal = String(e.apar_total_score);
    }

    const lastPromoDateVal = formatLastPromoDate(lastPromoDate);
    const candidateKey = e.candidateId ? String(e.candidateId) : (e.id ? String(e.id) : (e.empId || e.emp_no || String(idx)));

    return {
      ...e,
      key: candidateKey,
      id: candidateKey,
      empNo: e.empId || e.emp_no || e.empNo || "—",
      name: e.employeeName || e.empName || e.emp_name || "—",
      category: String(category),
      designation: String(desig),
      departmentName: String(e.departmentName || e.department_name || "-"),
      joiningDate: formatJoiningDate(joiningDate),
      lastPromoDate: lastPromoDateVal !== "No Data Found" ? lastPromoDateVal : (e.joiningOrLastPromoDate || "No Data Found"),
      joiningOrLastPromoDate: e.joiningOrLastPromoDate || "—",
      serviceYears: e.serviceYears !== undefined && e.serviceYears !== null ? String(e.serviceYears) : (e.service_years !== undefined && e.service_years !== null ? String(e.service_years) : "—"),
      aparTotal: aparTotal,
      aparRemarks: e.aparRemarks ?? e.apar_remarks ?? e.aparRemark ?? e.apar_remark ?? "—",
      effectiveDate: getEffectivePromotionDate(e),
      listClass: e.listClassification || e.list_classification || e.finalDecision || e.status || "",
      dpcStatus: e.dpcStatus || e.dpc_status || "PENDING",
      dpcRemarks: e.dpcRemarks || e.dpc_remarks || e.remarks || e.reason || ""
    };
  };

  const parsedEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];
    return employees.map((e: any, idx: number) => formatEmployeeRow(e, idx));
  }, [employees]);

  const recommendedCount = useMemo(() => {
    return parsedEmployees.filter((e: any) => e.dpcStatus === "RECOMMENDED").length;
  }, [parsedEmployees]);

  const notRecommendedCount = useMemo(() => {
    return parsedEmployees.filter((e: any) => e.dpcStatus === "NOT RECOMMENDED").length;
  }, [parsedEmployees]);

  const unselectedCount = useMemo(() => {
    return parsedEmployees.filter((emp) => {
      const isEvaluated = emp.dpcStatus === "RECOMMENDED" || emp.dpcStatus === "NOT RECOMMENDED";
      return !isEvaluated && !selectedRowKeys.includes(String(emp.id));
    }).length;
  }, [parsedEmployees, selectedRowKeys]);

  // Do not auto-select by default; retain selection while moving between pages.
  useEffect(() => {
    setSelectedRowKeys([]);
    tableRef.current?.resetSelection([]);
  }, [parsedEmployees]);

  const handleApplySelectedBulkRemark = () => {
    if (!selectedBulkRemark.trim()) return;
    const updatedRemarks = { ...remarks };
    selectedRowKeys.forEach((key) => {
      updatedRemarks[key] = selectedBulkRemark;
    });
    setRemarks(updatedRemarks);
    setSelectedBulkRemark("");
    toast.success(`Remarks applied to ${selectedRowKeys.length} selected employee(s).`);
  };

  const handleApplyUnselectedBulkRemark = () => {
    if (!unselectedBulkRemark.trim()) return;
    const updatedRemarks = { ...remarks };

    const unselected = parsedEmployees.filter((emp) => {
      const isEvaluated = emp.dpcStatus === "RECOMMENDED" || emp.dpcStatus === "NOT RECOMMENDED";
      return !isEvaluated && !selectedRowKeys.includes(String(emp.id));
    });

    unselected.forEach((emp) => {
      updatedRemarks[emp.id] = unselectedBulkRemark;
    });

    setRemarks(updatedRemarks);
    setUnselectedBulkRemark("");
    toast.success(`Remarks applied to ${unselected.length} unselected employee(s).`);
  };

  const handleClearAllRemarks = () => {
    setRemarks({});
    toast.success("Cleared all custom remarks.");
  };

  // Helper to format Date of Joining / Last Promotion as DD-MM-YY (e.g. 19-03-20)
  const formatDateDDMMYY = (dateVal: any): string => {
    if (dateVal === null || dateVal === undefined) return "—";
    const str = String(dateVal).trim();
    if (
      !str ||
      str === "—" ||
      str === "-" ||
      str.toLowerCase() === "n/a" ||
      str.toLowerCase() === "null" ||
      str.toLowerCase() === "undefined" ||
      str.toLowerCase() === "no data found" ||
      str.toLowerCase() === "invalid date"
    ) {
      return "—";
    }

    // Handle composite string e.g. "2020-03-19 / 2022-04-01"
    if (str.includes(" / ")) {
      const parts = str.split(" / ").map((p) => formatDateDDMMYY(p)).filter((p) => p !== "—");
      if (parts.length === 0) return "—";
      if (parts.length === 1) return parts[0];
      if (parts[0] === parts[1]) return parts[0];
      return parts.join(" / ");
    }

    const cleanDateStr = str.includes("T") ? str.split("T")[0].trim() : str;

    // Format 1: YYYY-MM-DD
    const ymdMatch = cleanDateStr.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
    if (ymdMatch) {
      const year = ymdMatch[1].slice(-2);
      const month = ymdMatch[2].padStart(2, "0");
      const day = ymdMatch[3].padStart(2, "0");
      return `${day}-${month}-${year}`;
    }

    // Format 2: DD-MM-YYYY
    const dmyMatch = cleanDateStr.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (dmyMatch) {
      const day = dmyMatch[1].padStart(2, "0");
      const month = dmyMatch[2].padStart(2, "0");
      const year = dmyMatch[3].slice(-2);
      return `${day}-${month}-${year}`;
    }

    // Format 3: DD-MM-YY
    const dmyShortMatch = cleanDateStr.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2})$/);
    if (dmyShortMatch) {
      const day = dmyShortMatch[1].padStart(2, "0");
      const month = dmyShortMatch[2].padStart(2, "0");
      const year = dmyShortMatch[3];
      return `${day}-${month}-${year}`;
    }

    // Fallback using Date constructor
    const d = new Date(cleanDateStr);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = String(d.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    }

    return "—";
  };

  // Helper to determine whether employee is confirmed (Yes / No)
  const getWhetherConfirmed = (c: any): string => {
    const candidateFields = [
      c.whetherConfirmed,
      c.whether_confirmed,
      c.isConfirmed,
      c.is_confirmed,
      c.whetherProbationConfirmed,
      c.whether_probation_confirmed,
      c.probationConfirmed,
      c.probation_confirmed,
      c.confirmationStatus,
      c.confirmation_status,
      c.confirmed,
      c.probationStatus,
      c.probation_status,
      c.confirmation_evaluation?.isConfirmed,
      c.confirmation_evaluation?.status,
      c.probation_evaluation?.isConfirmed,
      c.probation_evaluation?.status,
      c.employee?.whetherConfirmed,
      c.employee?.whetherProbationConfirmed,
      c.employee?.isConfirmed,
      c.employee?.probationConfirmed,
      c.employee?.confirmationStatus
    ];

    for (const val of candidateFields) {
      if (val !== undefined && val !== null && val !== "") {
        if (typeof val === "boolean") return val ? "Yes" : "No";
        const str = String(val).trim().toLowerCase();
        if (
          str === "yes" ||
          str === "y" ||
          str === "confirmed" ||
          str === "true" ||
          str === "clear" ||
          str === "cleared" ||
          str === "passed"
        ) {
          return "Yes";
        }
        if (
          str === "no" ||
          str === "n" ||
          str === "unconfirmed" ||
          str === "not confirmed" ||
          str === "false" ||
          str === "failed" ||
          str === "pending" ||
          str === "probation" ||
          str === "extended"
        ) {
          return "No";
        }
      }
    }

    const confDate =
      c.confirmationDate ??
      c.confirmation_date ??
      c.dateOfConfirmation ??
      c.date_of_confirmation ??
      c.adjustedConfirmationDate ??
      c.adjusted_confirmation_date ??
      c.employee?.confirmationDate ??
      c.employee?.dateOfConfirmation;

    if (
      confDate &&
      confDate !== "—" &&
      confDate !== "-" &&
      confDate !== "N/A" &&
      confDate !== "null" &&
      confDate !== "undefined" &&
      confDate !== "No Data Found"
    ) {
      return "Yes";
    }

    return "Yes";
  };

  // Helper to determine vigilance clearance (Yes / No)
  const getVigClearanceStatus = (c: any): string => {
    if (c.vigilance_evaluation?.vigilanceCleared !== undefined) {
      if (!c.vigilance_evaluation.vigilanceCleared) return "No";
    }
    if (c.vigilanceCleared !== undefined) {
      if (!c.vigilanceCleared) return "No";
    }
    if (c.criminal_evaluation?.criminalCleared !== undefined) {
      if (!c.criminal_evaluation.criminalCleared) return "No";
    }
    if (c.criminalCleared !== undefined) {
      if (!c.criminalCleared) return "No";
    }

    const crimCase = c.criminalCase ?? c.criminal_case;
    if (crimCase !== undefined && crimCase !== null) {
      const crimStr = String(crimCase).trim().toLowerCase();
      if (crimStr === "yes" || crimStr === "y" || crimStr === "true") return "No";
    }

    const vigStatus = (
      c.vigilance_evaluation?.status ??
      c.vigilanceStatus ??
      c.vigilance_status ??
      c.vigilanceType ??
      c.vigilance_type ??
      c.vigilance ??
      "CLEAR"
    ).toString().trim().toLowerCase();

    if (
      vigStatus.includes("pending") ||
      vigStatus.includes("initiated") ||
      vigStatus.includes("penalty") ||
      vigStatus.includes("adverse") ||
      vigStatus.includes("case") ||
      vigStatus === "no" ||
      vigStatus === "false" ||
      vigStatus === "failed"
    ) {
      return "No";
    }

    return "Yes";
  };

  // Helper to test if a string represents an actual recorded remark
  const isActualRemark = (val: any): boolean => {
    if (val === null || val === undefined) return false;
    const str = String(val).trim();
    if (!str) return false;
    const lower = str.toLowerCase();
    const cleanLower = lower.replace(/[.\s]+$/, "").trim();

    if (
      str === "—" ||
      str === "-" ||
      str === "." ||
      cleanLower === "n/a" ||
      cleanLower === "na" ||
      cleanLower === "null" ||
      cleanLower === "undefined" ||
      cleanLower === "none" ||
      cleanLower === "no remarks" ||
      cleanLower === "no remark" ||
      cleanLower === "no data found" ||
      cleanLower === "apar records not available" ||
      cleanLower === "apar record not available" ||
      cleanLower === "records not available" ||
      cleanLower === "record not available" ||
      cleanLower === "eligible for promotion" ||
      cleanLower === "ineligible for promotion" ||
      cleanLower === "dar clear" ||
      cleanLower === "vigilance clear" ||
      cleanLower === "vigilance: clear" ||
      cleanLower === "criminal case: no" ||
      cleanLower === "criminal case: clear" ||
      cleanLower === "no vigilance or criminal case pending" ||
      cleanLower === "no vigilance or criminal case" ||
      cleanLower === "no vigilance case pending" ||
      cleanLower === "no vigilance case" ||
      cleanLower === "no vigilance" ||
      cleanLower === "no criminal case pending" ||
      cleanLower === "no criminal case" ||
      cleanLower === "vigilance: no vigilance or criminal case pending" ||
      cleanLower === "clear" ||
      cleanLower === "cleared" ||
      cleanLower === "passed" ||
      cleanLower === "failed" ||
      cleanLower === "fit" ||
      cleanLower === "not fit" ||
      cleanLower.startsWith("service shortfall:") ||
      cleanLower.startsWith("apar below benchmark:") ||
      cleanLower.startsWith("d&ar case pending") ||
      cleanLower.startsWith("vigilance clearance pending") ||
      cleanLower.startsWith("vigilance: no vigilance")
    ) {
      return false;
    }
    return true;
  };

  // Helper to consolidate remarks from D&AR, Vigilance, APAR, and manual entry
  const getConsolidatedRemarks = (c: any, userManualRemark?: string): string => {
    const parts: string[] = [];

    const rawDarRemark =
      c.darRemarks ??
      c.dar_remarks ??
      c.darRemark ??
      c.dar_remark ??
      c.dar_evaluation?.remarks ??
      c.dar_evaluation?.darRemarks ??
      c.dar_evaluation?.dar_remarks ??
      c.dar_evaluation?.remark ??
      c.dar_evaluation?.comments;

    if (isActualRemark(rawDarRemark)) {
      const cleanDarRemark = String(rawDarRemark).trim();
      if (cleanDarRemark) {
        parts.push(`D&AR: ${cleanDarRemark}`);
      }
    }

    const rawVigRemark =
      c.vigilanceRemarks ??
      c.vigilance_remarks ??
      c.vigRemarks ??
      c.vig_remarks ??
      c.vigilance_evaluation?.remarks ??
      c.vigilance_evaluation?.vigilanceRemarks ??
      c.vigilance_evaluation?.vigilance_remarks ??
      c.vigilance_evaluation?.remark;

    if (isActualRemark(rawVigRemark)) {
      const cleanVigRemark = String(rawVigRemark).trim();
      if (cleanVigRemark) {
        parts.push(`Vigilance: ${cleanVigRemark}`);
      }
    } else {
      // Include vigilance information ONLY when an actual vigilance/criminal case exists
      const crimCase = c.criminalCase ?? c.criminal_case;
      const crimStr = crimCase !== undefined && crimCase !== null ? String(crimCase).trim().toLowerCase() : "";
      const isCrimCase = crimStr === "yes" || crimStr === "y" || crimStr === "true";

      const vigStatus = (
        c.vigilance_evaluation?.status ??
        c.vigilanceStatus ??
        c.vigilance_status ??
        c.vigilanceType ??
        c.vigilance_type ??
        c.vigilance ??
        ""
      ).toString().trim();
      const vigStatusLower = vigStatus.toLowerCase();
      const isAdverseVig = (
        vigStatusLower.includes("pending") ||
        vigStatusLower.includes("initiated") ||
        vigStatusLower.includes("penalty") ||
        vigStatusLower.includes("adverse") ||
        vigStatusLower.includes("case") ||
        vigStatusLower === "failed" ||
        vigStatusLower === "no"
      ) && !vigStatusLower.includes("no vigilance") && !vigStatusLower.includes("no case");

      if (isCrimCase && isAdverseVig) {
        parts.push(`Vigilance: Adverse case (${vigStatus}) & Criminal case pending`);
      } else if (isCrimCase) {
        parts.push("Vigilance: Criminal case pending");
      } else if (isAdverseVig) {
        parts.push(`Vigilance: ${vigStatus}`);
      }
    }

    const rawAparRemark =
      c.aparRemarks ??
      c.apar_remarks ??
      c.aparRemark ??
      c.apar_remark ??
      c.apar_remark;

    if (isActualRemark(rawAparRemark)) {
      const cleanAparRemark = String(rawAparRemark).trim();
      if (cleanAparRemark) {
        parts.push(`APAR: ${cleanAparRemark}`);
      }
    }

    if (userManualRemark && isActualRemark(userManualRemark)) {
      const cleanManualRemark = String(userManualRemark).trim();
      if (cleanManualRemark) {
        parts.push(`DPC: ${cleanManualRemark}`);
      }
    }

    if (parts.length === 0) return "";
    return parts.join("\n");
  };

  // Helper to format APAR Total with denominator based on assessed financial years
  const getAparTotalWithDenominator = (c: any, fyKeys: string[]): string => {
    let aparScores: Record<string, any> = {};
    const rawScores = c.apar_evaluation?.scores ?? c.apar_scores ?? c.aparScores ?? c.year_scores ?? c.yearScores;
    if (typeof rawScores === "string") {
      try { aparScores = JSON.parse(rawScores); } catch (e) {}
    } else if (rawScores && typeof rawScores === "object") {
      aparScores = rawScores;
    }

    let assessedYearsCount = 0;
    for (const fy of fyKeys) {
      const rawVal = aparScores[fy] !== undefined ? aparScores[fy] : c[fy];
      const formatted = formatAparScore(rawVal);
      if (formatted !== "-" && formatted !== "—" && formatted !== "") {
        const num = parseFloat(formatted);
        if (!isNaN(num) && num > 0) {
          assessedYearsCount++;
        }
      }
    }

    if (assessedYearsCount === 0 && Object.keys(aparScores).length > 0) {
      for (const [_, val] of Object.entries(aparScores)) {
        const rawVal = typeof val === "object" && val !== null ? (val.score ?? val.marks ?? val.value) : val;
        const formatted = formatAparScore(rawVal);
        if (formatted !== "-" && formatted !== "—" && formatted !== "") {
          const num = parseFloat(formatted);
          if (!isNaN(num) && num > 0) {
            assessedYearsCount++;
          }
        }
      }
    }

    const rawTotal = c.apar_evaluation?.totalScore ?? c.aparTotalScore ?? c.apar_total_score ?? c.aparTotal;
    const formattedTotal = formatAparScore(rawTotal);

    if (formattedTotal === "-" || formattedTotal === "—" || formattedTotal === "") {
      return "-";
    }

    if (String(formattedTotal).includes("/")) {
      return String(formattedTotal);
    }

    let denominator = assessedYearsCount * 10;
    const explicitMax = c.apar_evaluation?.maxScore ?? c.apar_evaluation?.maxMarks ?? c.apar_evaluation?.totalMaxScore;
    if (explicitMax !== undefined && explicitMax !== null && Number(explicitMax) > 0) {
      denominator = Number(explicitMax);
    } else if (c.apar_evaluation?.applicableYears !== undefined && Number(c.apar_evaluation.applicableYears) > 0) {
      denominator = Number(c.apar_evaluation.applicableYears) * 10;
    }

    if (denominator > 0) {
      return `${formattedTotal}/${denominator}`;
    }

    return formattedTotal;
  };

  const getExportData = () => {
    const listToExport = filtered.length > 0 ? filtered : parsedEmployees;
    if (!listToExport || listToExport.length === 0) {
      return null;
    }

    const currentCycleObj = cyclesList.find((c: any) => Number(c.id) === Number(selectedCycleId));
    
    // Dynamic Title parameters
    const empType = currentCycleObj?.cadre || currentCycleObj?.employeeType || currentCycleObj?.gradeFrom || "Non-Executive";
    const reqYears = currentCycleObj?.requiredServiceYears || currentCycleObj?.minServiceYears || "05";
    let period = "";
    if (currentCycleObj?.quarter && currentCycleObj?.financialYear) {
      period = `${currentCycleObj.quarter} ${currentCycleObj.financialYear}`;
    } else if (currentCycleObj?.quarter || currentCycleObj?.financialYear) {
      period = `${currentCycleObj?.quarter || ""} ${currentCycleObj?.financialYear || ""}`.trim();
    } else {
      period = currentCycleObj?.name || "2025-2026";
    }

    const titleText = `Assessment Sheet for Promotion of ${empType} staff on completion of ${String(reqYears).padStart(2, "0")} years of service (${period})`;
    const subtitleText = "Revised APAR Criteria: Reserved Category-26/40 and Others-28/40";

    // Dynamic APAR Headers Extraction (4 keys)
    const extractFyKeys = (candidates: any[]) => {
      const fySet = new Set<string>();
      candidates.forEach((c) => {
        let aparScores: Record<string, any> = {};
        const rawScores = c.apar_evaluation?.scores ?? c.apar_scores ?? c.aparScores ?? c.year_scores ?? c.yearScores;
        if (typeof rawScores === "string") {
          try { aparScores = JSON.parse(rawScores); } catch (e) {}
        } else if (rawScores && typeof rawScores === "object") {
          aparScores = rawScores;
        }
        Object.keys(aparScores).forEach((k) => fySet.add(k));
      });

      const sortedFy = Array.from(fySet).sort();
      if (sortedFy.length >= 4) return sortedFy.slice(-4);
      if (sortedFy.length > 0) return sortedFy;
      return ["2022-23", "2023-24", "2024-25", "2025-26"];
    };

    const fyKeys = extractFyKeys(listToExport);

    // Columns in exact order (20 columns after removing APAR Remarks and APAR Result)
    const headers = [
      "Sl. No.",
      "Emp No.",
      "Name",
      "Desig.",
      "Cat.",
      "Pay Scale",
      "Date of Joining / Last Promotion",
      "D&AR Clearance",
      "Vig. Clearance",
      "Whether Confirmed (Yes/No)",
      "LWP / LOP / Suspended Period",
      "Date of Eligibility for Promotion",
      `FY ${fyKeys[0] || "2022-23"}`,
      `FY ${fyKeys[1] || "2023-24"}`,
      `FY ${fyKeys[2] || "2024-25"}`,
      `FY ${fyKeys[3] || "2025-26"}`,
      "APAR Total",
      "Required APAR Benchmark",
      "Remarks",
      "Suitable / Not Suitable"
    ];

    // PDF Table Headers (Single-line FY headers, Whether Confirmed, APAR Total)
    const pdfHeaders = [
      "Sl.\nNo.",
      "Emp\nNo.",
      "Name",
      "Desig.",
      "Cat.",
      "Pay\nScale",
      "Date of Joining /\nLast Promotion",
      "D&AR\nClearance",
      "Vig.\nClearance",
      "Whether Confirmed\n(Yes/No)",
      "LWP / LOP /\nSuspension",
      "Eligibility\nDate",
      `FY ${fyKeys[0] || "2022-23"}`,
      `FY ${fyKeys[1] || "2023-24"}`,
      `FY ${fyKeys[2] || "2024-25"}`,
      `FY ${fyKeys[3] || "2025-26"}`,
      "APAR\nTotal",
      "Req.\nBench.",
      "Remarks",
      "Suitable /\nNot Suitable"
    ];

    const rows = listToExport.map((c: any, idx: number) => {
      const empNo = c.empId || c.emp_no || c.empNo || "—";
      const name = c.employeeName || c.empName || c.emp_name || c.name || "—";
      const desig = c.oldDesignation || c.old_designation || c.designationName || c.designation || "—";
      const cat = c.category || c.emp_category || c.empCategory || c.caste || "—";
      const payScale = c.currentPayscale || c.current_payscale || c.currentGrade || c.payScale || c.pay_scale || "—";
      
      const rawJoiningDate = c.joiningDate || c.joining_date || c.date_of_joining || c.dateOfJoining || c.doj || "";
      const rawLastPromoDate = c.lastPromoDate || c.last_promotion_date || c.last_promo_date || c.lastPromotion || "";
      const rawCombined = c.joiningOrLastPromoDate || c.joining_or_last_promo_date || "";

      let dojLastPromo = "";
      if (rawCombined && rawCombined !== "—" && rawCombined !== "-") {
        dojLastPromo = formatDateDDMMYY(rawCombined);
      } else {
        const jDate = formatDateDDMMYY(rawJoiningDate);
        const pDate = formatDateDDMMYY(rawLastPromoDate);
        if (jDate !== "—" && pDate !== "—") {
          dojLastPromo = jDate === pDate ? jDate : `${jDate} / ${pDate}`;
        } else if (pDate !== "—") {
          dojLastPromo = pDate;
        } else if (jDate !== "—") {
          dojLastPromo = jDate;
        } else {
          dojLastPromo = "—";
        }
      }

      // D&AR Clearance multiline cell content
      const rawDarStatus = (c.darStatus ?? c.dar_status ?? c.dar_evaluation?.status ?? c.dar ?? "").toString().trim();
      const darStatusLower = rawDarStatus.toLowerCase();

      // Check if actual case number exists
      const rawCaseNo = c.caseNo ?? c.case_no ?? c.dar_evaluation?.caseNo;
      let validCaseNo = "";
      if (rawCaseNo !== undefined && rawCaseNo !== null) {
        const strCaseNo = String(rawCaseNo).trim();
        const lowerCaseNo = strCaseNo.toLowerCase();
        if (
          strCaseNo &&
          strCaseNo !== "—" &&
          strCaseNo !== "-" &&
          strCaseNo !== "." &&
          lowerCaseNo !== "n/a" &&
          lowerCaseNo !== "na" &&
          lowerCaseNo !== "null" &&
          lowerCaseNo !== "undefined" &&
          lowerCaseNo !== "none" &&
          lowerCaseNo !== "no case" &&
          lowerCaseNo !== "no data found"
        ) {
          validCaseNo = strCaseNo;
        }
      }

      const csDate = formatDateDMY(c.chargeSheetIssuedDate ?? c.charge_sheet_issued_date ?? c.dar_evaluation?.chargeSheetIssuedDate);
      const penaltyDesc = c.penaltyDescription ?? c.penalty_description ?? c.dar_evaluation?.penaltyDescription ?? "—";
      const dateOfAction = formatDateDMY(c.dateOfAction ?? c.date_of_action ?? c.dar_evaluation?.dateOfAction);
      const startDate = formatDateDMY(c.penaltyStartDate ?? c.penalty_start_date ?? c.dar_evaluation?.penaltyStartDate);
      const endDate = formatDateDMY(c.penaltyEndDate ?? c.penalty_end_date ?? c.dar_evaluation?.penaltyEndDate);

      const darLines: string[] = [];
      if (darStatusLower === "dar clear" || darStatusLower === "dar_clear" || darStatusLower === "clear" || darStatusLower === "cleared" || darStatusLower === "passed") {
        darLines.push("DAR CLEAR");
        if (validCaseNo) {
          darLines.push(`Case No.: ${validCaseNo}`);
        }
      } else if (darStatusLower.includes("chargesheet")) {
        darLines.push("ChargeSheet Issued");
        if (validCaseNo) {
          darLines.push(`Case No.: ${validCaseNo}`);
        }
        if (csDate && csDate !== "—") {
          darLines.push(`Charge Sheet Date: ${csDate}`);
        }
      } else if (darStatusLower.includes("minor")) {
        darLines.push("Minor Penalty");
        if (validCaseNo) {
          darLines.push(`Case No.: ${validCaseNo}`);
        }
        if (dateOfAction && dateOfAction !== "—") {
          darLines.push(`Date of Action: ${dateOfAction}`);
        }
        if (penaltyDesc && penaltyDesc !== "—") {
          darLines.push(`Penalty: ${penaltyDesc}`);
        }
      } else if (darStatusLower.includes("major")) {
        darLines.push("Major Penalty");
        if (validCaseNo) {
          darLines.push(`Case No.: ${validCaseNo}`);
        }
        if (startDate && startDate !== "—") {
          darLines.push(`Start Date: ${startDate}`);
        }
        if (endDate && endDate !== "—") {
          darLines.push(`End Date: ${endDate}`);
        }
        if (penaltyDesc && penaltyDesc !== "—") {
          darLines.push(`Penalty: ${penaltyDesc}`);
        }
      } else {
        darLines.push(rawDarStatus || "Pending");
        if (validCaseNo) {
          darLines.push(`Case No.: ${validCaseNo}`);
        }
      }

      const darTextCell = darLines.join('<br style="mso-data-placement:same-cell;"/>');

      // Vigilance Clearance - display only Yes or No
      const vigClearance = getVigClearanceStatus(c);

      // Confirmation Status - display only Yes or No
      const whetherConfirmed = getWhetherConfirmed(c);

      const suitableNotSuitable = ""; // Left blank for manual DPC decision/signature

      // LWP / LOP / Suspended Period
      const lwp = c.lwpDays ?? c.lwp_days ?? c.lwp ?? 0;
      const suspension = c.suspensionDays ?? c.suspension_days ?? c.suspension ?? 0;
      let lwpCell = "0 days";
      if (lwp > 0 && suspension > 0) lwpCell = `LWP: ${lwp} days, Suspension: ${suspension} days`;
      else if (lwp > 0) lwpCell = `LWP: ${lwp} days`;
      else if (suspension > 0) lwpCell = `Suspension: ${suspension} days`;

      // Date of Eligibility
      const eligDate = getEffectivePromotionDate(c);

      // APAR scores per FY
      let aparScores: Record<string, any> = {};
      const rawScores = c.apar_evaluation?.scores ?? c.apar_scores ?? c.aparScores ?? c.year_scores ?? c.yearScores;
      if (typeof rawScores === "string") {
        try { aparScores = JSON.parse(rawScores); } catch (e) {}
      } else if (rawScores && typeof rawScores === "object") {
        aparScores = rawScores;
      }

      const fyScore1 = formatAparScore(aparScores[fyKeys[0]]);
      const fyScore2 = formatAparScore(aparScores[fyKeys[1]]);
      const fyScore3 = formatAparScore(aparScores[fyKeys[2]]);
      const fyScore4 = formatAparScore(aparScores[fyKeys[3]]);

      // Total APAR Marks formatted with applicable maximum (e.g. 26/30, 35/40)
      const aparTotal = getAparTotalWithDenominator(c, fyKeys);
      const reqBenchmark = c.apar_evaluation?.requiredBenchmark ?? c.aparBenchmark ?? "32.00";

      // Consolidated Remarks: populated ONLY with actual recorded remarks, otherwise blank
      const consolidatedRemarks = getConsolidatedRemarks(c, remarks[c.id]);

      return {
        srNo: idx + 1,
        empNo,
        name,
        desig,
        cat,
        payScale,
        dojLastPromo,
        darTextCell,
        vigClearance,
        whetherConfirmed,
        lwpCell,
        eligDate,
        fyScore1,
        fyScore2,
        fyScore3,
        fyScore4,
        aparTotal,
        reqBenchmark,
        consolidatedRemarks,
        suitableNotSuitable
      };
    });

    const m0Name = (committeeMembers[0]?.name || "________________").trim();
    const m0Desig = committeeMembers[0]?.designation ? `(${committeeMembers[0].designation.trim()})` : "(Designation)";
    const m1Name = (committeeMembers[1]?.name || "________________").trim();
    const m1Desig = committeeMembers[1]?.designation ? `(${committeeMembers[1].designation.trim()})` : "(Designation)";
    const m2Name = (committeeMembers[2]?.name || "________________").trim();
    const m2Desig = committeeMembers[2]?.designation ? `(${committeeMembers[2].designation.trim()})` : "(Designation)";

    const cycleLabel = currentCycleObj?.name ? currentCycleObj.name.replace(/[^a-zA-Z0-9_-]/g, "_") : `cycle_${selectedCycleId}`;
    const dateStr = new Date().toISOString().split("T")[0];

    return {
      titleText,
      subtitleText,
      currentCycleObj,
      cycleLabel,
      dateStr,
      headers,
      pdfHeaders,
      rows,
      m0Name,
      m0Desig,
      m1Name,
      m1Desig,
      m2Name,
      m2Desig
    };
  };

  const handleDownloadPDF = () => {
    try {
      const exportData = getExportData();
      if (!exportData) {
        toast.warning("No candidate data available to export.");
        return;
      }

      const {
        titleText,
        subtitleText,
        currentCycleObj,
        cycleLabel,
        dateStr,
        pdfHeaders,
        rows,
        m0Name,
        m0Desig,
        m1Name,
        m1Desig,
        m2Name,
        m2Desig
      } = exportData;

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4"
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginSide = 10;

      // 20 columns matching pdfHeaders (APAR Remarks and APAR Result removed)
      const bodyRows = rows.map((r) => [
        String(r.srNo),
        String(r.empNo),
        String(r.name),
        String(r.desig),
        String(r.cat),
        String(r.payScale),
        String(cleanCellText(r.dojLastPromo)),
        String(cleanCellText(r.darTextCell)),
        String(r.vigClearance),
        String(r.whetherConfirmed),
        String(cleanCellText(r.lwpCell)),
        String(r.eligDate),
        String(r.fyScore1),
        String(r.fyScore2),
        String(r.fyScore3),
        String(r.fyScore4),
        String(r.aparTotal),
        String(r.reqBenchmark),
        String(cleanCellText(r.consolidatedRemarks)),
        String(r.suitableNotSuitable)
      ]);

      // Total width: 16+36+58+46+32+46+46+46+32+46+38+42+34+34+34+34+32+26+98+42 = 818 pt
      // Margin left: 10, right: 10. Printable: 821.89 pt (A4 landscape = 841.89 pt) -> Fits cleanly with zero clipping
      const colStyles: { [key: number]: any } = {
        0: { halign: "center", cellWidth: 16 }, // Sl. No.
        1: { halign: "center", cellWidth: 36, fontStyle: "bold" }, // Emp No.
        2: { halign: "left", cellWidth: 58, fontStyle: "bold" }, // Name
        3: { halign: "left", cellWidth: 46 }, // Desig.
        4: { halign: "center", cellWidth: 32 }, // Cat. (Single line for GENERAL)
        5: { halign: "center", cellWidth: 46 }, // Pay Scale
        6: { halign: "center", cellWidth: 46 }, // DOJ / Last Promo (DD-MM-YY)
        7: { halign: "left", cellWidth: 46 }, // D&AR Clearance
        8: { halign: "center", cellWidth: 32, fontStyle: "bold" }, // Vig. Clearance (Yes/No)
        9: { halign: "center", cellWidth: 46, fontStyle: "bold" }, // Whether Confirmed (Yes/No)
        10: { halign: "center", cellWidth: 38 }, // LWP / LOP / Suspension
        11: { halign: "center", cellWidth: 42 }, // Eligibility Date
        12: { halign: "center", cellWidth: 34 }, // FY1 (Single line)
        13: { halign: "center", cellWidth: 34 }, // FY2 (Single line)
        14: { halign: "center", cellWidth: 34 }, // FY3 (Single line)
        15: { halign: "center", cellWidth: 34 }, // FY4 (Single line)
        16: { halign: "center", cellWidth: 32, fontStyle: "bold" }, // APAR Total (Single line e.g. 26/30)
        17: { halign: "center", cellWidth: 26 }, // Req. Bench.
        18: { halign: "left", minCellWidth: 90 }, // Remarks (expanded dynamically to maximize space for handwritten remarks)
        19: { halign: "center", cellWidth: 42 } // Suitable / Not Suitable
      };

      // Limit PDF pagination to exactly 10 employee entries per page
      const PAGE_SIZE = 10;
      const chunks: any[][] = [];
      for (let i = 0; i < bodyRows.length; i += PAGE_SIZE) {
        chunks.push(bodyRows.slice(i, i + PAGE_SIZE));
      }
      if (chunks.length === 0) {
        chunks.push([]);
      }

      // Dynamic vertical layout calculation based on page height, headers, margins, and signature area:
      // Page height = 595.28 pt (A4 Landscape)
      const tableStartY = 48;
      const desigY = pageHeight - 13;
      const nameY = pageHeight - 25;
      const signatureSpace = 27; // Clear height for committee members' handwritten signatures
      const footerDividerY = nameY - signatureSpace; // 543.28 pt
      const gapAboveFooter = 8; // Clean small gap above signature area
      const tableBottomTarget = footerDividerY - gapAboveFooter; // 535.28 pt

      // Calculate row height from actual available height between table start and signature area
      const actualHeaderHeight = 23.5; // Table column headers height (2 lines at font size 5.5 + padding)
      const availableBodyHeight = tableBottomTarget - tableStartY - actualHeaderHeight; // 463.78 pt
      const dynamicRowHeight = Math.floor((availableBodyHeight / PAGE_SIZE) * 10) / 10; // 46.3 pt

      // autoTable bottom margin ensures table fits within the reserved content area without unwanted page breaks
      const autoTableBottomMargin = pageHeight - footerDividerY + 2; // ~54 pt

      chunks.forEach((chunkRows, pageIdx) => {
        if (pageIdx > 0) {
          doc.addPage("a4", "landscape");
        }

        autoTable(doc, {
          head: [pdfHeaders],
          body: chunkRows,
          startY: tableStartY,
          margin: { top: tableStartY, bottom: autoTableBottomMargin, left: marginSide, right: marginSide },
          showHead: "everyPage",
          theme: "grid",
          styles: {
            font: "helvetica",
            fontSize: 5.5,
            cellPadding: 2,
            minCellHeight: dynamicRowHeight,
            lineColor: [180, 180, 180],
            lineWidth: 0.5,
            overflow: "linebreak",
            valign: "middle",
            textColor: [20, 20, 20]
          },
          headStyles: {
            fillColor: [24, 95, 165],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 5.5,
            halign: "center",
            valign: "middle",
            minCellHeight: 18,
            cellPadding: 2,
            lineColor: [15, 23, 42],
            lineWidth: 0.5
          },
          bodyStyles: {
            minCellHeight: dynamicRowHeight
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252]
          },
          columnStyles: colStyles
        });
      });

      const totalPages = (doc.internal as any).getNumberOfPages();

      // Stamp page header and committee member footer on EVERY page
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);

        // --- PAGE HEADER (Header repeated on each page) ---
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        doc.setTextColor(15, 23, 42);
        doc.text(titleText, pageWidth / 2, 18, { align: "center" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(71, 85, 105);
        doc.text(subtitleText, pageWidth / 2, 29, { align: "center" });

        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);
        const cycleName = currentCycleObj?.name ? `Cycle: ${currentCycleObj.name}` : `DPC Cycle #${selectedCycleId}`;
        doc.text(cycleName, marginSide, 40);
        doc.text(`Page ${p} of ${totalPages}`, pageWidth - marginSide, 40, { align: "right" });

        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.75);
        doc.line(marginSide, 44, pageWidth - marginSide, 44);

        // --- PAGE FOOTER (Committee member signatures present on every page) ---
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.75);
        doc.line(marginSide, footerDividerY, pageWidth - marginSide, footerDividerY);

        const col1X = marginSide + 25;
        const col2X = pageWidth / 2;
        const col3X = pageWidth - marginSide - 25;

        // Committee Member 1 (Left) - Name & Designation only
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(15, 23, 42);
        doc.text(m0Name, col1X, nameY, { align: "left" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(71, 85, 105);
        doc.text(m0Desig, col1X, desigY, { align: "left" });

        // Committee Member 2 (Center) - Name & Designation only
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(15, 23, 42);
        doc.text(m1Name, col2X, nameY, { align: "center" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(71, 85, 105);
        doc.text(m1Desig, col2X, desigY, { align: "center" });

        // Committee Member 3 (Right) - Name & Designation only
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(15, 23, 42);
        doc.text(m2Name, col3X, nameY, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(71, 85, 105);
        doc.text(m2Desig, col3X, desigY, { align: "right" });
      }

      doc.save(`DPC_Assessment_Sheet_${cycleLabel}_${dateStr}.pdf`);
      toast.success("DPC Assessment Sheet (PDF) downloaded successfully!");
    } catch (e) {
      console.error("Failed to download PDF", e);
      toast.error("Failed to download PDF assessment sheet");
    }
  };

  const handleDownloadCandidates = () => {
    try {
      const exportData = getExportData();
      if (!exportData) {
        toast.warning("No candidate data available to export.");
        return;
      }

      const {
        titleText,
        subtitleText,
        cycleLabel,
        dateStr,
        headers,
        rows,
        m0Name,
        m0Desig,
        m1Name,
        m1Desig,
        m2Name,
        m2Desig
      } = exportData;

      const totalCols = headers.length; // 20 columns
      const col1 = Math.floor(totalCols / 3);
      const col2 = Math.floor(totalCols / 3);
      const col3 = totalCols - col1 - col2;

      const htmlTable = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8" />
  <!--[if gte mso 9]>
  <xml>
    <x:ExcelWorkbook>
      <x:ExcelWorksheets>
        <x:ExcelWorksheet>
          <x:Name>DPC Assessment Sheet</x:Name>
          <x:WorksheetOptions>
            <x:Print>
              <x:ValidPrinterInfo/>
              <x:PaperSizeIndex>9</x:PaperSizeIndex>
              <x:Scale>65</x:Scale>
            </x:Print>
            <x:PageSetup>
              <x:Layout x:Orientation="Landscape"/>
              <x:Header x:Data="&amp;C&amp;&quot;Calibri,Bold&quot;&amp;11 ${escapeExcelXml(titleText)}&amp;#10;&amp;&quot;Calibri,Regular&quot;&amp;9 ${escapeExcelXml(subtitleText)}&amp;R&amp;P of &amp;N"/>
              <x:Footer x:Data="&amp;L&amp;&quot;Calibri,Bold&quot;${escapeExcelXml(m0Name)}&amp;#10;${escapeExcelXml(m0Desig)}&amp;C&amp;&quot;Calibri,Bold&quot;${escapeExcelXml(m1Name)}&amp;#10;${escapeExcelXml(m1Desig)}&amp;R&amp;&quot;Calibri,Bold&quot;${escapeExcelXml(m2Name)}&amp;#10;${escapeExcelXml(m2Desig)}"/>
              <x:RepeatRows x:Data="$1:$3"/>
            </x:PageSetup>
            <x:DisplayGridlines/>
            <x:FreezePanes/>
            <x:FrozenNoSplit/>
            <x:SplitHorizontal>3</x:SplitHorizontal>
            <x:TopRowBottomPane>3</x:TopRowBottomPane>
            <x:ActivePane>2</x:ActivePane>
          </x:WorksheetOptions>
        </x:ExcelWorksheet>
      </x:ExcelWorksheets>
    </x:ExcelWorkbook>
  </xml>
  <![endif]-->
  <style>
    @page {
      size: landscape;
      margin: 0.5in 0.3in 0.6in 0.3in;
      mso-page-orientation: landscape;
      mso-header-data: "&C&\"Calibri,Bold\"&11 ${escapeExcelXml(titleText)}\\000A&\"Calibri,Regular\"&9 ${escapeExcelXml(subtitleText)}&R&P of &N";
      mso-footer-data: "&L&\"Calibri,Bold\"${escapeExcelXml(m0Name)}\\000A&\"Calibri,Regular\"${escapeExcelXml(m0Desig)}&C&\"Calibri,Bold\"${escapeExcelXml(m1Name)}\\000A&\"Calibri,Regular\"${escapeExcelXml(m1Desig)}&R&\"Calibri,Bold\"${escapeExcelXml(m2Name)}\\000A&\"Calibri,Regular\"${escapeExcelXml(m2Desig)}";
    }
    table { border-collapse: collapse; width: 100%; font-family: Calibri, Arial, sans-serif; font-size: 9.5pt; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    tr { page-break-inside: avoid; }
    th.title-header { background-color: #1e3a8a !important; color: #ffffff !important; font-size: 13pt; font-weight: bold; text-align: center; padding: 12px; border: 1.5px solid #1e3a8a !important; }
    th.subtitle-header { background-color: #f1f5f9 !important; color: #334155 !important; font-size: 9.5pt; font-weight: bold; text-align: center; padding: 6px; border: 1px solid #cbd5e1 !important; }
    th.col-header { background-color: #185FA5 !important; color: #ffffff !important; font-weight: bold; text-align: center; border: 1px solid #0f172a !important; padding: 8px 6px; font-size: 9.5pt; vertical-align: middle; white-space: nowrap; }
    td { border: 1px solid #cbd5e1 !important; padding: 6px 8px; vertical-align: middle; font-size: 9.5pt; color: #0f172a; }
    .text-center { text-align: center; }
    .text-right { text-align: right; }
    .text-bold { font-weight: bold; }
    .row-even { background-color: #f8fafc; }
    .fit-cell { background-color: #dcfce7 !important; color: #15803d !important; font-weight: bold; text-align: center; }
    .not-fit-cell { background-color: #fee2e2 !important; color: #b91c1c !important; font-weight: bold; text-align: center; }
  </style>
</head>
<body>
  <table border="1">
    <thead>
      <tr>
        <th colspan="${totalCols}" class="title-header">${titleText}</th>
      </tr>
      <tr>
        <th colspan="${totalCols}" class="subtitle-header">${subtitleText}</th>
      </tr>
      <tr>
        ${headers.map((h) => `<th class="col-header">${h}</th>`).join("")}
      </tr>
    </thead>
    <tbody>
      ${rows
        .map((r, idx) => {
          const rowClass = idx % 2 === 1 ? ' class="row-even"' : "";
          const confClass = r.whetherConfirmed === "Yes" ? 'class="fit-cell"' : 'class="not-fit-cell"';
          const vigClass = r.vigClearance === "Yes" ? 'class="text-center text-bold"' : 'class="not-fit-cell"';
          const isPageBreak = (idx + 1) % 10 === 0 && idx + 1 < rows.length;
          const trStyle = `height: 46pt; mso-height-source: userset;${isPageBreak ? " page-break-after: always; mso-special-format: page-break;" : ""}`;

          return `<tr${rowClass} style="${trStyle}">
            <td class="text-center">${r.srNo}</td>
            <td style="mso-number-format:'\\@';" class="text-center text-bold">${r.empNo}</td>
            <td class="text-bold">${r.name}</td>
            <td class="text-center">${r.desig}</td>
            <td class="text-center" style="white-space: nowrap;">${r.cat}</td>
            <td class="text-center">${r.payScale}</td>
            <td class="text-center" style="white-space: nowrap;">${r.dojLastPromo}</td>
            <td style="white-space: pre-wrap; mso-data-placement: same-cell;">${r.darTextCell}</td>
            <td ${vigClass}>${r.vigClearance}</td>
            <td ${confClass}>${r.whetherConfirmed}</td>
            <td class="text-center">${r.lwpCell}</td>
            <td class="text-center">${r.eligDate}</td>
            <td class="text-center" style="white-space: nowrap;">${r.fyScore1}</td>
            <td class="text-center" style="white-space: nowrap;">${r.fyScore2}</td>
            <td class="text-center" style="white-space: nowrap;">${r.fyScore3}</td>
            <td class="text-center" style="white-space: nowrap;">${r.fyScore4}</td>
            <td class="text-center text-bold" style="white-space: nowrap;">${r.aparTotal}</td>
            <td class="text-center">${r.reqBenchmark}</td>
            <td style="white-space: pre-wrap; mso-data-placement: same-cell; min-width: 180px; width: 220px;">${r.consolidatedRemarks}</td>
            <td class="text-center">${r.suitableNotSuitable}</td>
          </tr>`;
        })
        .join("\n")}
    </tbody>
    <tfoot>
      <tr><td colspan="${totalCols}" style="border:none !important; height: 25px;"></td></tr>
      <tr><td colspan="${totalCols}" style="border:none !important; height: 25px;"></td></tr>
      <tr>
        <td colspan="${col1}" class="text-center text-bold" style="border:none !important; font-size: 11pt; padding-top: 15px;">
          ${escapeXml(m0Name)}
        </td>
        <td colspan="${col2}" class="text-center text-bold" style="border:none !important; font-size: 11pt; padding-top: 15px;">
          ${escapeXml(m1Name)}
        </td>
        <td colspan="${col3}" class="text-center text-bold" style="border:none !important; font-size: 11pt; padding-top: 15px;">
          ${escapeXml(m2Name)}
        </td>
      </tr>
      <tr>
        <td colspan="${col1}" class="text-center" style="border:none !important; font-size: 10pt; color: #334155;">
          ${escapeXml(m0Desig)}
        </td>
        <td colspan="${col2}" class="text-center" style="border:none !important; font-size: 10pt; color: #334155;">
          ${escapeXml(m1Desig)}
        </td>
        <td colspan="${col3}" class="text-center" style="border:none !important; font-size: 10pt; color: #334155;">
          ${escapeXml(m2Desig)}
        </td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

      const blob = new Blob([htmlTable], {
        type: "application/vnd.ms-excel;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `DPC_Assessment_Sheet_${cycleLabel}_${dateStr}.xls`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("DPC Assessment Sheet (Excel) downloaded successfully!");
    } catch (e) {
      console.error("Failed to download Excel", e);
      toast.error("Failed to download Excel assessment sheet");
    }
  };

  const handleSubmitDpcReview = async () => {
    setSubmitting(true);
    try {
      const recommendations = parsedEmployees.map((emp) => {
        let isRecommended = selectedRowKeys.includes(String(emp.id));
        if (emp.dpcStatus === "RECOMMENDED") {
          isRecommended = true;
        } else if (emp.dpcStatus === "NOT RECOMMENDED") {
          isRecommended = false;
        }
        return {
          candidateId: Number(emp.id),
          recommend: isRecommended,
          remarks: remarks[emp.id] !== undefined ? remarks[emp.id] : emp.dpcRemarks || " "
        };
      });

      const payload = {
        cycleId: selectedCycleId,
        recommendations
      };

      console.log("Submitting DPC Review:", payload);

      const response = await Api.post(API_URLS.PROMOTION.DPC_REVIEW, payload, true);
      console.log("DPC_REVIEW response:", response);

      toast.success("DPC review submitted successfully!");
      fetchChecks();
    } catch (error: any) {
      console.error("Failed to submit DPC review", error);
      toast.error("Failed to submit DPC review: " + Api.getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const currentCycle = useMemo(() => {
    return cyclesList.find(c => Number(c.id) === selectedCycleId);
  }, [cyclesList, selectedCycleId]);

  const isReadOnly = useMemo(() => {
    if (parsedEmployees.length > 0 && parsedEmployees.every((e: any) => e.dpcStatus === "RECOMMENDED" || e.dpcStatus === "NOT RECOMMENDED")) {
      return true;
    }
    if (!currentCycle) return false;
    const status = (currentCycle.status || "").toUpperCase();
    return status === "APPROVED" || status === "SAP_SYNCED" || status === "COMPLETED";
  }, [currentCycle, parsedEmployees]);

  const filtered = useMemo(() => {
    let result = parsedEmployees;

    // Apply active filter
    if (activeFilter === "RECOMMENDED") {
      result = result.filter(e => e.dpcStatus === "RECOMMENDED");
    } else if (activeFilter === "NOT_RECOMMENDED") {
      result = result.filter(e => e.dpcStatus === "NOT RECOMMENDED");
    }

    if (!query) return result;
    const q = query.toLowerCase();
    return result.filter((e: EmployeeRow) =>
      (e.name || "").toLowerCase().includes(q) ||
      (e.empNo || "").toLowerCase().includes(q) ||
      (e.designation || "").toLowerCase().includes(q)
    );
  }, [query, parsedEmployees, activeFilter]);

  const handleExclude = async (record: EmployeeRow) => {
    const result = await Swal.fire({
      title: "Exclude employee?",
      text: `${record.name} (${record.empNo}) will move to List A and leave DPC Review.`,
      input: "textarea",
      inputLabel: "Exclusion reason",
      inputPlaceholder: "Enter the reason for exclusion",
      inputAttributes: { maxlength: "2000" },
      showCancelButton: true,
      confirmButtonText: "Exclude",
      confirmButtonColor: "#dc3545",
      inputValidator: (value: string) => !value?.trim() ? "An exclusion reason is required." : undefined
    });
    if (!result.isConfirmed) return;
    setExcludingId(record.id);
    try {
      await Api.post(API_URLS.PROMOTION.EXCLUDE_CANDIDATE, {
        cycleId: Number(selectedCycleId), candidateId: Number(record.id), reason: String(result.value).trim()
      }, true);
      setEmployees(previous => previous.filter(candidate => String(candidate.candidateId || candidate.id) !== record.id));
      setSelectedRowKeys(previous => previous.filter(key => String(key) !== record.id));
      setRemarks(previous => { const next = { ...previous }; delete next[record.id]; return next; });
      setPagination(previous => ({ ...previous, current: 1 }));
      toast.success("Employee excluded and moved to List A.");
      await fetchChecks();
    } catch (error: any) {
      toast.error("Could not exclude employee: " + Api.getErrorMessage(error));
    } finally {
      setExcludingId(null);
    }
  };

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: "Sr. No.",
        dataIndex: "srNo",
        render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
        width: 70
      },
      { title: "Emp No.", dataIndex: "empNo" },
      { title: "Name", dataIndex: "name", render: (v: string) => <div style={{ fontWeight: 600 }}>{v}</div> },
      { title: "Category", dataIndex: "category" },
      { title: "Designation", dataIndex: "designation" },
      { title: "Employee Department", dataIndex: "departmentName" },
      { title: "Date of Joining", dataIndex: "joiningDate" },
      { title: "Last Promo Date", dataIndex: "lastPromoDate" },
      { title: "Service (Years)", dataIndex: "serviceYears" },
      {
        title: "APAR Total",
        dataIndex: "aparTotal",
        render: (v: string, record: EmployeeRow) => {
          const isFailed = record.apar_evaluation?.status === "FAILED" || record.apar_evaluation?.isSuitable === false;
          return <span className={isFailed ? "text-danger fw-bold" : "text-success fw-bold"}>{formatAparScore(v)}</span>;
        }
      },
      {
        title: "APAR Remarks",
        dataIndex: "aparRemarks",
        render: (v: string, record: EmployeeRow) => {
          const remarks = v || record.aparRemarks || record.apar_remarks || record.aparRemark || record.apar_remark || "—";
          return (
            <div style={{ maxWidth: "220px", whiteSpace: "normal", wordBreak: "break-word" }}>
              {remarks}
            </div>
          );
        }
      },
      {
        title: "D&AR",
        dataIndex: "dar",
        render: (_: any, record: EmployeeRow) => {
          const darBadge = getDarBadgeDetails(record);
          return <span className={`badge ${darBadge.badgeClass}`} style={darBadge.badgeStyle}>{darBadge.text}</span>;
        }
      },
      {
        title: "Vigilance",
        dataIndex: "vigilance",
        render: (_: any, record: EmployeeRow) => {
          const vigBadge = getVigilanceBadgeDetails(record);
          return <span className={`badge ${vigBadge.badgeClass}`} style={vigBadge.badgeStyle}>{vigBadge.text}</span>;
        }
      },
      { title: "Promotion Effective Date", dataIndex: "effectiveDate" },
      {
        title: "Evaluation Details",
        dataIndex: "evaluationDetails",
        render: (_: any, record: EmployeeRow) => (
          <button
            type="button"
            className="btn btn-xs btn-outline-primary d-inline-flex align-items-center gap-1 py-1 px-2.5"
            style={{ fontSize: "11px", borderRadius: "5px", whiteSpace: "nowrap" }}
            onClick={() => {
              setSelectedEvaluationCandidate(record);
              setActiveModalTab("apar");
            }}
          >
            <i className="ti ti-eye fs-6"></i> View Details
          </button>
        )
      },
      {
        title: "DPC Status",
        dataIndex: "dpcStatus",
        render: (v: string) => (
          <span className={`badge ${v === "RECOMMENDED" ? "bg-success" : v === "NOT RECOMMENDED" ? "bg-danger" : "bg-warning"}`}>{v}</span>
        )
      },
      {
        title: "Remarks",
        dataIndex: "remarks",
        render: (_: any, record: EmployeeRow) => (
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Enter remark"
            value={remarks[record.id] !== undefined ? remarks[record.id] : record.dpcRemarks}
            onChange={(e) => {
              setRemarks(prev => ({
                ...prev,
                [record.id]: e.target.value
              }));
            }}
            disabled={isReadOnly || record.dpcStatus === "RECOMMENDED" || record.dpcStatus === "NOT RECOMMENDED"}
            style={{ minWidth: 200 }}
          />
        )
      }
    ];

    cols.push({
      title: "Action",
      key: "action",
      render: (_: any, record: EmployeeRow) => (
        <button type="button" className="btn btn-outline-danger btn-sm"
          disabled={excludingId !== null || submitting || ["COMPLETED", "APPROVED", "SAP_SYNCED"].includes((currentCycle?.status || "").toUpperCase()) || record.approvalStatus === "APPROVED" || Boolean(record.officeOrderNo || record.office_order_no)}
          onClick={() => handleExclude(record)}>
          {excludingId === record.id ? "Excluding..." : "Exclude"}
        </button>
      )
    });
    return cols;
  }, [remarks, isReadOnly, pagination, excludingId, submitting, currentCycle, selectedCycleId]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">DPC Review</h3>
            <p className="text-muted small mb-0">Evaluate and recommend eligible candidates for promotion</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              value={selectedCycleId}
              onChange={(e) => handleCycleChange(Number(e.target.value))}
              style={{ minWidth: 240, height: 36 }}
            >
              {cyclesList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {!isReadOnly && (
              <button
                className="btn btn-success btn-sm"
                onClick={handleSubmitDpcReview}
                disabled={submitting || excludingId !== null || parsedEmployees.length === 0}
                style={{ height: 36, whiteSpace: 'nowrap' }}
              >
                {submitting ? "Submitting..." : "Submit DPC Review"}
              </button>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <h5 className="mb-3">Eligible Candidates</h5>

            {/* SEARCH AND FILTER BAR */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3 p-3 bg-light rounded-3 shadow-xs">
              <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: "560px" }}>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="ti ti-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search name, emp no or designation..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                {(recommendedCount > 0 || notRecommendedCount > 0) && (
                  <span className="text-muted small fw-bold text-uppercase me-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Filter:</span>
                )}
                {recommendedCount > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-2 px-3 py-1.5 rounded-pill transition-all fw-semibold ${activeFilter === "RECOMMENDED" ? "btn-success text-white shadow-sm" : "btn-outline-success"
                      }`}
                    onClick={() => setActiveFilter(activeFilter === "RECOMMENDED" ? "ALL" : "RECOMMENDED")}
                    style={{ fontSize: "12px", borderStyle: activeFilter === "RECOMMENDED" ? "solid" : "dashed" }}
                  >
                    <i className="ti ti-circle-check" style={{ fontSize: "14px" }}></i>
                    <span>Recommended</span>
                    <span className={`badge rounded-pill ${activeFilter === "RECOMMENDED" ? "bg-white text-success" : "bg-success text-white"}`}>
                      {recommendedCount}
                    </span>
                  </button>
                )}
                {notRecommendedCount > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-2 px-3 py-1.5 rounded-pill transition-all fw-semibold ${activeFilter === "NOT_RECOMMENDED" ? "btn-danger text-white shadow-sm" : "btn-outline-danger"
                      }`}
                    onClick={() => setActiveFilter(activeFilter === "NOT_RECOMMENDED" ? "ALL" : "NOT_RECOMMENDED")}
                    style={{ fontSize: "12px", borderStyle: activeFilter === "NOT_RECOMMENDED" ? "solid" : "dashed" }}
                  >
                    <i className="ti ti-circle-x" style={{ fontSize: "14px" }}></i>
                    <span>Not Recommended</span>
                    <span className={`badge rounded-pill ${activeFilter === "NOT_RECOMMENDED" ? "bg-white text-danger" : "bg-danger text-white"}`}>
                      {notRecommendedCount}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* BULK REMARKS ACTIONS */}
            {!isReadOnly && (
              <div className="p-2 mb-3 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <div className="d-flex align-items-center gap-1 me-2">
                    <i className="ti ti-edit text-muted" style={{ fontSize: "14px" }}></i>
                    <span className="fw-semibold text-dark" style={{ fontSize: "12px" }}>Bulk Remarks:</span>
                  </div>

                  {/* Selected */}
                  <div className="d-flex align-items-center gap-1" style={{ flex: "1 1 280px", maxWidth: "400px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm bg-white"
                      placeholder="Remarks for selected..."
                      value={selectedBulkRemark}
                      onChange={(e) => setSelectedBulkRemark(e.target.value)}
                      style={{ fontSize: "12px", height: "30px" }}
                    />
                    <button
                      className="btn btn-primary btn-sm px-2.5"
                      onClick={handleApplySelectedBulkRemark}
                      disabled={!selectedBulkRemark.trim() || selectedRowKeys.length === 0}
                      style={{ fontSize: "11px", height: "30px", whiteSpace: "nowrap" }}
                    >
                      Apply Selected ({selectedRowKeys.length})
                    </button>
                  </div>

                  <div className="d-none d-md-block" style={{ width: "1px", height: "20px", backgroundColor: "#e2e8f0", margin: "0 8px" }}></div>

                  {/* Unselected */}
                  <div className="d-flex align-items-center gap-1" style={{ flex: "1 1 280px", maxWidth: "400px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm bg-white"
                      placeholder="Remarks for unselected..."
                      value={unselectedBulkRemark}
                      onChange={(e) => setUnselectedBulkRemark(e.target.value)}
                      style={{ fontSize: "12px", height: "30px" }}
                    />
                    <button
                      className="btn btn-secondary text-white btn-sm px-2.5"
                      onClick={handleApplyUnselectedBulkRemark}
                      disabled={!unselectedBulkRemark.trim() || unselectedCount === 0}
                      style={{ fontSize: "11px", height: "30px", whiteSpace: "nowrap" }}
                    >
                      Apply Unselected ({unselectedCount})
                    </button>
                  </div>

                  <button
                    className="btn btn-link btn-sm text-muted ms-auto py-0"
                    onClick={handleClearAllRemarks}
                    style={{ fontSize: "11px", textDecoration: "none" }}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            <Table
              ref={tableRef}
              columns={columns}
              dataSource={filtered}
              paginationConfig={pagination}
              Selection={true}
              selectAllAcrossPages={true}
              onRowSelection={(keys) => setSelectedRowKeys(keys)}
              loading={loading}
              scroll={{ x: "max-content" }}
              handlePagination={(pag: any) => {
                setPagination({
                  current: pag.current,
                  pageSize: pag.pageSize
                });
              }}
              getCheckboxProps={(record: any) => {
                const isEvaluated = record.dpcStatus === "RECOMMENDED" || record.dpcStatus === "NOT RECOMMENDED";
                return {
                  disabled: isEvaluated,
                  className: isEvaluated ? "d-none" : "",
                };
              }}
            >
              <div className="d-flex align-items-center justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                  style={{ height: "31px", fontSize: "12px", borderRadius: "6px" }}
                  onClick={() => setShowExportModal(true)}
                  title="Download Candidates (PDF / Excel)"
                >
                  <i className="ti ti-download" style={{ fontSize: "13px" }} />
                  <span>Download (PDF / Excel)</span>
                </button>
                <div id="DataTables_Table_0_filter" className="dataTables_filter mb-0">
                  <label className="mb-0">
                    <input
                      type="search"
                      className="form-control form-control-sm"
                      placeholder="Search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </label>
                </div>
              </div>
            </Table>
          </div>
        </div>

        {/* Evaluation Details Modal */}
        {selectedEvaluationCandidate && (() => {
          const c = selectedEvaluationCandidate;
          const empIdVal = String(c.empNo || c.empId || c.emp_no || "—");
          const empNameVal = String(c.name || c.employeeName || c.empName || c.emp_name || "—");

          // Parse APAR scores
          let aparScores: Record<string, any> = {};
          const rawAparScores = c.apar_evaluation?.scores ?? c.apar_scores ?? c.aparScores ?? c.year_scores ?? c.yearScores;
          if (typeof rawAparScores === "string") {
            try {
              aparScores = JSON.parse(rawAparScores);
            } catch (e) {
              aparScores = {};
            }
          } else if (rawAparScores && typeof rawAparScores === "object") {
            aparScores = rawAparScores;
          }

          // D&AR status details
          const dar = getDarBadgeDetails(c);
          const rawDarStatus = (c.darStatus ?? c.dar_status ?? c.dar_evaluation?.status ?? "").toString().trim();
          const darStatusLower = rawDarStatus.toLowerCase();
          const isChargeSheet = darStatusLower === "chargesheet issued" || darStatusLower === "chargesheet_issued" || darStatusLower === "chargesheetissued";
          const isMinor = darStatusLower === "minor penalty" || darStatusLower === "minor_penalty" || darStatusLower === "minorpenalty";
          const isMajor = darStatusLower === "major penalty" || darStatusLower === "major_penalty" || darStatusLower === "majorpenalty";
          const isClearOrNone = !isChargeSheet && !isMinor && !isMajor;

          const darCaseNo = c.caseNo ?? c.case_no ?? c.dar_evaluation?.caseNo ?? "—";
          const darChargeSheetDate = formatDateDMY(c.chargeSheetIssuedDate ?? c.charge_sheet_issued_date ?? c.dar_evaluation?.chargeSheetIssuedDate);
          const darPenaltyDesc = c.penaltyDescription ?? c.penalty_description ?? c.dar_evaluation?.penaltyDescription ?? "—";
          const darDateOfAction = formatDateDMY(c.dateOfAction ?? c.date_of_action ?? c.dar_evaluation?.dateOfAction);
          const darPenaltyStart = formatDateDMY(c.penaltyStartDate ?? c.penalty_start_date ?? c.dar_evaluation?.penaltyStartDate);
          const darPenaltyEnd = formatDateDMY(c.penaltyEndDate ?? c.penalty_end_date ?? c.dar_evaluation?.penaltyEndDate);

          // Vigilance details
          const vigStatus = c.vigilance_evaluation?.status ?? c.vigilanceStatus ?? c.vigilance_status ?? "—";
          const vigClearedStr = c.vigilance_evaluation?.vigilanceCleared !== undefined
            ? (c.vigilance_evaluation.vigilanceCleared ? "Cleared" : "Not Cleared")
            : (c.vigilanceCleared !== undefined ? (c.vigilanceCleared ? "Cleared" : "Not Cleared") : "—");

          const crimStatus = c.criminal_evaluation?.status ?? c.criminalStatus ?? c.criminal_status ?? "—";
          const crimClearedStr = c.criminal_evaluation?.criminalCleared !== undefined
            ? (c.criminal_evaluation.criminalCleared ? "Cleared" : "Not Cleared")
            : (c.criminalCleared !== undefined ? (c.criminalCleared ? "Cleared" : "Not Cleared") : "—");

          const vigType = c.vigilanceType ?? c.vigilance_type ?? "—";
          const crimCaseVal = c.criminalCase !== undefined
            ? (c.criminalCase === "yes" || c.criminalCase === "Yes" ? "Yes" : (c.criminalCase === "no" || c.criminalCase === "No" ? "No" : String(c.criminalCase)))
            : (c.criminal_case !== undefined ? (c.criminal_case === "yes" || c.criminal_case === "Yes" ? "Yes" : (c.criminal_case === "no" || c.criminal_case === "No" ? "No" : String(c.criminal_case))) : "—");
          const vigRemarks = c.vigilanceRemarks ?? c.vigilance_remarks ?? "—";

          return (
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1055 }}
              onClick={() => setSelectedEvaluationCandidate(null)}
            >
              <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                style={{ maxWidth: "620px" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px" }}>
                  {/* Header */}
                  <div className="modal-header border-bottom py-3 px-4 bg-light" style={{ borderRadius: "14px 14px 0 0" }}>
                    <div>
                      <h6 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0" style={{ fontSize: "16px" }}>
                        <i className="ti ti-file-analytics text-primary fs-5"></i>
                        Candidate Evaluation Details
                      </h6>
                      <div className="text-muted small mt-1">
                        Employee: <span className="fw-bold text-dark">{empNameVal}</span> ({empIdVal})
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSelectedEvaluationCandidate(null)}
                      aria-label="Close"
                    ></button>
                  </div>

                  {/* Navigation Tabs */}
                  <div className="px-4 pt-3 bg-white border-bottom">
                    <ul className="nav nav-tabs nav-tabs-bottom border-0 gap-3 mb-0">
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "apar" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("apar")}
                        >
                          <i className="ti ti-file-text me-1.5"></i> APAR Details
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "dar" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("dar")}
                        >
                          <i className="ti ti-scale me-1.5"></i> D&AR Details
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "vigilance" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("vigilance")}
                        >
                          <i className="ti ti-shield-check me-1.5"></i> Vigilance Details
                        </button>
                      </li>
                    </ul>
                  </div>

                  {/* Body Content */}
                  <div className="modal-body p-4" style={{ minHeight: "260px" }}>
                    {/* Tab 1: APAR Details */}
                    {activeModalTab === "apar" && (
                      <div>
                        <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "14px" }}>
                          Financial Year APAR Marks
                        </h6>
                        {Object.keys(aparScores).length === 0 ? (
                          <div className="alert alert-light border text-muted small p-3 rounded">
                            No individual financial year APAR scores provided.
                          </div>
                        ) : (
                          <div className="table-responsive mb-3">
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "14px" }}>
                              <thead style={{ backgroundColor: "#f1f5f9" }}>
                                <tr>
                                  <th className="py-2.5 px-3" style={{ color: "#334155", fontWeight: 700, fontSize: "13px" }}>Financial Year</th>
                                  <th className="py-2.5 px-3 text-end" style={{ color: "#334155", fontWeight: 700, fontSize: "13px" }}>APAR Score</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(aparScores).map(([fy, score]) => {
                                  const rawScoreVal = typeof score === "object" && score !== null ? (score.score ?? score.marks ?? score.value ?? score.rating) : score;
                                  const numericScore = parseFloat(String(rawScoreVal));
                                  const isZero = isNaN(numericScore) || numericScore === 0;

                                  return (
                                    <tr key={fy}>
                                      <td className="py-2.5 px-3 fw-semibold text-dark" style={{ fontSize: "14px" }}>{fy}</td>
                                      <td className="py-2.5 px-3 text-end fw-bold" style={{ fontSize: "16px", color: isZero ? "#64748b" : "#2563eb" }}>
                                        {isZero ? "-" : String(rawScoreVal ?? "-")}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        <div className="row g-3 mt-1">
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Total APAR Score</div>
                              <div className="fw-bold mt-1" style={{ fontSize: "22px", lineHeight: "1.2", color: (c.apar_evaluation?.status === "FAILED" || c.apar_evaluation?.isSuitable === false) ? "#dc2626" : "#16a34a" }}>
                                {formatAparScore(c.apar_evaluation?.totalScore ?? c.aparTotalScore ?? c.apar_total_score ?? c.aparTotal)}
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Required Benchmark</div>
                              <div className="fw-bold text-dark mt-1" style={{ fontSize: "22px", lineHeight: "1.2" }}>
                                {c.apar_evaluation?.requiredBenchmark ?? c.aparBenchmark ?? "32.00"}
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>APAR Result</div>
                              <div>
                                <span className={`badge ${c.apar_evaluation?.status === "FAILED" ? "bg-danger" : "bg-success"}`} style={{ fontSize: "12px", padding: "5px 12px", fontWeight: 700 }}>
                                  {c.apar_evaluation?.status ?? c.aparStatus ?? "PASSED"}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>APAR Suitability</div>
                              <div className="fw-bold" style={{ fontSize: "15px", color: (c.apar_evaluation?.isSuitable === false) ? "#dc2626" : "#16a34a" }}>
                                {c.apar_evaluation?.isSuitable !== undefined
                                  ? (c.apar_evaluation.isSuitable ? "Suitable" : "Not Suitable")
                                  : (c.isSuitable !== undefined ? (c.isSuitable ? "Suitable" : "Not Suitable") : "Suitable")}
                              </div>
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>APAR Remarks</div>
                              <div className="fw-semibold text-dark" style={{ fontSize: "13px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {c.aparRemarks ?? c.apar_remarks ?? c.aparRemark ?? c.apar_remark ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tab 2: D&AR Details */}
                    {activeModalTab === "dar" && (
                      <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">D&AR Status</span>
                          <span className={`badge ${dar.badgeClass}`} style={dar.badgeStyle}>
                            {dar.text}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Case No.</span>
                          <span className="fw-semibold text-dark">{darCaseNo}</span>
                        </div>

                        {(isChargeSheet || isClearOrNone) && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <span className="text-secondary font-medium">Charge Sheet Issued Date</span>
                            <span className="fw-semibold text-dark">{darChargeSheetDate}</span>
                          </div>
                        )}

                        {(isMinor || isMajor || isClearOrNone) && (
                          <div className="pb-2 border-bottom">
                            <span className="text-secondary font-medium d-block mb-1">Penalty Description</span>
                            <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                              {darPenaltyDesc}
                            </div>
                          </div>
                        )}

                        {(isMinor || isClearOrNone) && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <span className="text-secondary font-medium">Date of Action</span>
                            <span className="fw-semibold text-dark">{darDateOfAction}</span>
                          </div>
                        )}

                        {(isMajor || isClearOrNone) && (
                          <>
                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                              <span className="text-secondary font-medium">Penalty Start Date</span>
                              <span className="fw-semibold text-dark">{darPenaltyStart}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                              <span className="text-secondary font-medium">Penalty End Date</span>
                              <span className="fw-semibold text-dark">{darPenaltyEnd}</span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Tab 3: Vigilance Details */}
                    {activeModalTab === "vigilance" && (
                      <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Status</span>
                          <span className="fw-semibold text-dark">{vigStatus}</span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Cleared</span>
                          <span className={`badge ${vigClearedStr === "Cleared" ? "bg-success" : "bg-danger"}`}>
                            {vigClearedStr}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Criminal Case Status</span>
                          <span className="fw-semibold text-dark">{crimStatus}</span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Criminal Case Cleared</span>
                          <span className={`badge ${crimClearedStr === "Cleared" ? "bg-success" : "bg-danger"}`}>
                            {crimClearedStr}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Type</span>
                          <span className="fw-semibold text-dark">{vigType}</span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Criminal Case</span>
                          <span className="fw-semibold text-dark">{crimCaseVal}</span>
                        </div>

                        <div className="pb-2">
                          <span className="text-secondary font-medium d-block mb-1">Vigilance Remarks</span>
                          <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                            {vigRemarks}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Export & Committee Members Configuration Modal */}
        {showExportModal && (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1060 }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content shadow-lg border-0">
                <div className="modal-header bg-primary text-white py-3">
                  <h5 className="modal-title fs-16 fw-semibold text-white d-flex align-items-center gap-2">
                    <i className="ti ti-file-export fs-18"></i>
                    Configure DPC Committee Members for Assessment Sheet
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowExportModal(false)}
                  />
                </div>
                <div className="modal-body p-4">
                  <div className="alert alert-info py-2 px-3 mb-4 d-flex align-items-center gap-2" style={{ fontSize: "12px", borderRadius: "6px" }}>
                    <i className="ti ti-info-circle fs-16 flex-shrink-0"></i>
                    <span>
                      Search each Committee Member by Employee Number. The Name and Designation will be auto-fetched from the employee master. These 3 members will be printed on every page footer of the exported PDF and Excel assessment sheets.
                    </span>
                  </div>

                  <div className="row g-3">
                    {[0, 1, 2].map((idx) => {
                      const member = committeeMembers[idx];
                      const titleLabel = idx === 0 ? "Committee Member 1 (Chairperson)" : `Committee Member ${idx + 1}`;
                      const isSearching = searchingIndex === idx;

                      return (
                        <div key={idx} className="col-md-4">
                          <div className="card h-100 border shadow-sm" style={{ borderRadius: "8px" }}>
                            <div className="card-header bg-light py-2 px-3 fw-bold text-dark border-bottom d-flex align-items-center justify-content-between" style={{ fontSize: "13px" }}>
                              <span className="d-flex align-items-center">
                                <i className="ti ti-user me-1.5 text-primary"></i>
                                {titleLabel}
                              </span>
                              {member.name ? (
                                <span className="badge bg-success text-white" style={{ fontSize: "10px", padding: "3px 6px" }}>
                                  <i className="ti ti-check me-1"></i>Verified
                                </span>
                              ) : null}
                            </div>
                            <div className="card-body p-3 d-flex flex-column gap-2.5">
                              <div>
                                <label className="form-label mb-1 text-secondary small fw-medium">Emp No / ID</label>
                                <div className="input-group input-group-sm">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g. 00001234"
                                    value={member.empNo}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setCommitteeMembers((prev) => {
                                        const updated = [...prev];
                                        updated[idx] = { ...updated[idx], empNo: val };
                                        return updated;
                                      });
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleSearchMember(idx);
                                      }
                                    }}
                                  />
                                  <button
                                    className="btn btn-primary d-flex align-items-center justify-content-center"
                                    type="button"
                                    disabled={isSearching}
                                    onClick={() => handleSearchMember(idx)}
                                    title="Search Employee"
                                    style={{ width: "38px" }}
                                  >
                                    {isSearching ? (
                                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    ) : (
                                      <i className="ti ti-search" />
                                    )}
                                  </button>
                                </div>
                              </div>

                              <div>
                                <label className="form-label mb-1 text-secondary small fw-medium">Employee Name</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm bg-light"
                                  placeholder="Search Emp No to auto-fill"
                                  value={member.name}
                                  readOnly={true}
                                  style={{ cursor: "not-allowed", fontWeight: member.name ? 600 : 400 }}
                                />
                              </div>

                              <div>
                                <label className="form-label mb-1 text-secondary small fw-medium">Designation</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm bg-light"
                                  placeholder="Auto-filled from search"
                                  value={member.designation}
                                  readOnly={true}
                                  style={{ cursor: "not-allowed" }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="modal-footer bg-light py-2.5 px-4 d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowExportModal(false)}
                  >
                    Cancel
                  </button>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm d-inline-flex align-items-center gap-1.5 px-3"
                      onClick={() => {
                        setShowExportModal(false);
                        handleDownloadPDF();
                      }}
                    >
                      <i className="ti ti-file-type-pdf fs-15"></i>
                      <span>Download PDF</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-success btn-sm d-inline-flex align-items-center gap-1.5 px-3"
                      onClick={() => {
                        setShowExportModal(false);
                        handleDownloadCandidates();
                      }}
                    >
                      <i className="ti ti-file-spreadsheet fs-15"></i>
                      <span>Download Excel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DpcReview;
